import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addNote, deleteNote, updateNote } from '../../store/notesSlice';
import { addHighlight, deleteHighlight } from '../../store/highlightSlice';
import { makeHighlightChapterKey } from '../../utils/annotationKeys';
import { usePassageAnnotations } from '../../hooks/usePassageAnnotations';
import InteractiveVerse from './InteractiveVerse';
import VerseActionMenu from './VerseActionMenu';
import NoteModal from '../NoteModal';
import HighlightModal from '../HighlightModal';
import CustomAlert from '../../utils/CustomAlert';

const COLORS = [
  { label: 'Yellow', value: '#fef08a' },
  { label: 'Mint', value: '#bbf7d0' },
  { label: 'Lavender', value: '#e9d5ff' },
];

const cleanText = (value = '') => value.replace(/\s+/g, ' ').trim();

const splitTitleFromVerse = (text = '') => {
  const match = text.match(/^<(?!span\b)([^>]+)>\s*(.*)$/);
  if (!match) return { title: null, body: text };
  return { title: match[1], body: match[2] || '' };
};

const InteractivePassage = ({ verses = [], version, user, actions = ['memo', 'highlight', 'copy'], onRequireLogin }) => {
  const dispatch = useDispatch();
  const { highlightColors } = useSelector((state) => state.settings || {});
  const colors = useMemo(() => (Array.isArray(highlightColors) && highlightColors.length >= 3
    ? COLORS.map((item, index) => ({ ...item, value: highlightColors[index] })) : COLORS), [highlightColors]);
  const { getMemo, getHighlights } = usePassageAnnotations({ verses, version, user });
  const [active, setActive] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showColors, setShowColors] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState(null);
  const [highlightModalOpen, setHighlightModalOpen] = useState(false);
  const [alert, setAlert] = useState('');
  const menuRef = useRef(null);
  const selectedVerseRef = useRef(null);

  const requireUser = useCallback(() => {
    if (onRequireLogin) onRequireLogin();
    else setAlert('Please log in to use memos and highlights.');
    return false;
  }, [onRequireLogin]);

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    setShowColors(false);
    selectedVerseRef.current?.focus?.();
  }, []);

  useEffect(() => {
    if (menuOpen) menuRef.current?.querySelector('button')?.focus();
  }, [menuOpen]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && menuOpen) closeMenu();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [closeMenu, menuOpen]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const handlePointerDown = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) closeMenu();
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [closeMenu, menuOpen]);

  const positionFor = (event, rect) => {
    const source = rect || event?.currentTarget?.getBoundingClientRect?.();
    if (!source) return { top: 80, left: 20 };
    const width = 210;
    const left = Math.min(Math.max(8, source.left), window.innerWidth - width - 8);
    const top = source.bottom + 8 + 180 < window.innerHeight ? source.bottom + 8 : Math.max(8, source.top - 188);
    return { top, left };
  };

  const handleVerseClick = useCallback((event, verseId, book, chapter, selectedVersion, columnId, verseElement) => {
    const element = verseElement || event?.currentTarget;
    selectedVerseRef.current = element;
    const fullText = cleanText(element?.textContent || '');
    const selection = window.getSelection();
    let selectedText = fullText;
    let startOffset = 0;
    let endOffset = fullText.length;
    let rect = element?.getBoundingClientRect?.();
    if (selection?.rangeCount && !selection.isCollapsed && element && element.contains(selection.anchorNode) && element.contains(selection.focusNode)) {
      const range = selection.getRangeAt(0);
      const before = range.cloneRange();
      before.selectNodeContents(element);
      before.setEnd(range.startContainer, range.startOffset);
      selectedText = cleanText(range.toString());
      startOffset = before.toString().length;
      endOffset = startOffset + range.toString().length;
      rect = range.getBoundingClientRect();
    }
    setActive({ verseId, book, chapter, version: selectedVersion, selectedText, startOffset, endOffset, verseText: fullText, position: positionFor(event, rect) });
    setMenuOpen(true);
    setShowColors(false);
  }, []);

  const handleHighlightClick = useCallback((event, highlight, verseId, book, chapter, selectedVersion, columnId, verseElement) => {
    event.preventDefault();
    event.stopPropagation();
    const rect = event.target?.getBoundingClientRect?.() || verseElement?.getBoundingClientRect?.();
    setActive({ verseId, book, chapter, version: selectedVersion, selectedText: highlight.selected_text, startOffset: Number(highlight.start_offset), endOffset: Number(highlight.end_offset), verseText: cleanText(verseElement?.textContent || ''), existingHighlight: highlight, position: positionFor(event, rect) });
    setMenuOpen(true);
    setShowColors(false);
  }, []);

  const copyVerse = () => {
    if (!active) return;
    navigator.clipboard?.writeText(`${active.book} ${active.chapter}:${active.verseId % 1000} ${active.selectedText} (${active.version})`);
    setAlert(`${active.version} verse copied to clipboard.`);
    closeMenu();
  };

  const openNote = () => {
    if (!user) return requireUser();
    setNote(getMemo(active.verseId));
    setNoteOpen(true);
    closeMenu();
  };

  const saveNote = async (text, title) => {
    if (!user || !active) return;
    if (note) {
      await dispatch(updateNote({ id: note.id, text, title, book: active.book, version: active.version })).unwrap();
    } else {
      await dispatch(addNote({ userId: user.id, verseId: active.verseId, content: text, title, book: active.book, version: active.version })).unwrap();
    }
  };

  const removeNote = async () => {
    if (note) await dispatch(deleteNote(note.id)).unwrap();
    setNoteOpen(false);
  };

  const saveHighlight = async (color) => {
    if (!user || !active) return requireUser();
    const chapterKey = makeHighlightChapterKey(active.version, active.book, active.chapter);
    try {
      if (active.existingHighlight) {
        await dispatch(deleteHighlight({ highlightId: active.existingHighlight.id, chapterKey })).unwrap();
      }
      await dispatch(addHighlight({
        chapterKey,
        newHighlight: {
          version: active.version, book: active.book, chapter: active.chapter,
          verseId: active.verseId, selectedText: active.selectedText,
          startOffset: active.startOffset, endOffset: active.endOffset,
          scope: active.startOffset === 0 && active.endOffset >= active.verseText.length ? 'verse' : 'text',
          color,
        },
      })).unwrap();
      closeMenu();
    } catch (error) {
      setAlert(error.message || 'Could not save highlight.');
    }
  };

  const showExistingHighlights = () => {
    if (!user) return requireUser();
    if (active && getHighlights(active.verseId).length) setHighlightModalOpen(true);
    else setShowColors(true);
  };

  const handleUnmappedHighlightClick = (event, highlight) => {
    event.preventDefault();
    event.stopPropagation();
    const versions = (highlight?.mapped_versions || []).join(', ');
    setAlert(`This highlight is mapped in ${versions || 'another translation'}. Select the matching words here to map it.`);
  };

  return (
    <>
      <div className="space-y-3 text-lg leading-8 text-gray-900">
        {verses.length ? verses.map((verse) => {
          const memo = getMemo(verse.verse_id);
          const highlights = getHighlights(verse.verse_id);
          const { title, body } = splitTitleFromVerse(verse.text || '');
          return (
            <div key={verse.id || verse.verse_id}>
              {title && <div className="my-4 font-bold text-m">{title}</div>}
              <InteractiveVerse
                verse={{ ...verse, version, text: body }}
                verseText={body}
                highlights={highlights}
                highlightColor={colors[0].value}
                memo={memo}
                onVerseClick={handleVerseClick}
                onNoteClick={() => {
                  if (!user) { requireUser(); return; }
                  setActive({ verseId: verse.verse_id, book: verse.book, chapter: verse.chapter, version });
                  setNote(getMemo(verse.verse_id));
                  setNoteOpen(true);
                }}
                onHighlightClick={handleHighlightClick}
                onUnmappedHighlightClick={handleUnmappedHighlightClick}
                isMenuActive={menuOpen && active?.verseId === verse.verse_id}
              />
            </div>
          );
        }) : <p className="text-base text-gray-600">No Bible text is available for this version yet.</p>}
      </div>

      {menuOpen && active && (
        <div ref={menuRef} className="fixed z-50 min-w-[190px] rounded-md border border-gray-300 bg-white p-2 shadow-lg" style={{ top: active.position.top, left: active.position.left }}>
          <VerseActionMenu
            actions={actions}
            active={active}
            showColors={showColors}
            colors={colors}
            onMemo={openNote}
            onHighlight={(color) => (color ? saveHighlight(color) : showExistingHighlights())}
            onCopy={copyVerse}
            onDelete={() => { dispatch(deleteHighlight({ highlightId: active.existingHighlight.id, chapterKey: makeHighlightChapterKey(active.version, active.book, active.chapter) })); closeMenu(); }}
          />
        </div>
      )}

      <NoteModal isOpen={noteOpen} onClose={() => setNoteOpen(false)} book={active?.book} chapter={active?.chapter} verseNumber={active?.verseId || 0} note={note} onSave={saveNote} onDelete={removeNote} />
      <HighlightModal isOpen={highlightModalOpen} highlights={active ? getHighlights(active.verseId) : []} onClose={() => setHighlightModalOpen(false)} onDelete={(id) => dispatch(deleteHighlight({ highlightId: id, chapterKey: makeHighlightChapterKey(active.version, active.book, active.chapter) }))} book={active?.book} chapter={active?.chapter} verseNumber={active?.verseId || 0} />
      {alert && <CustomAlert message={alert} onClose={() => setAlert('')} />}
    </>
  );
};

export default InteractivePassage;
